// widget/src/hooks/useMediaQuery.ts
//
// Minimal media-query hook. Subscribes to a `window.matchMedia` MediaQueryList
// and returns its boolean `.matches`. Used by FinChatWidget to pick the
// bottom-sheet branch on mobile (`max-width: 767px`) vs the desktop canvas.
//
// Returns false on SSR / non-browser environments, matching the existing
// behavior of `useMobileSheet` which also no-ops outside the browser.

import { useEffect, useState } from "react"

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mql = window.matchMedia(query)
    // Initial sync in case state from the lazy initializer is stale (rare,
    // but possible if `query` was resolved at a different SSR boundary).
    setMatches(mql.matches)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    // addEventListener has been the canonical API since Safari 14; the
    // .addListener fallback is for very old browsers — Pearl already
    // requires modern Safari for visualViewport, so we don't fall back.
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [query])

  return matches
}
